import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Grid, List, Typography } from '@mui/material';
import {
    AttributeAssociationItem,
    defaultAttributeAssociationItem,
    EDGE_CONDITION_ERRORS,
    getEmptyErrorMessage,
} from '../../edge-definition-helper';
import { AttributeAssociationSelection } from './AttributeAssociationSelection';
import {
    AttributeAssociationWidgetProps,
    attributeAssociationWidgetStyles,
} from './create-edge-definition-helper';
import { Fab } from '@biq/apex-ui';

export const AttributeAssociationWidget = ({
    handleAttributeAssociationsChanged,
    attributeAssociationList,
    leftSideSelectionOptions = [],
    rightSideSelectionOptions = [],
}: AttributeAssociationWidgetProps): React.JSX.Element => {
    const isAttributeAssociationNonEmpty: boolean =
        attributeAssociationList.every(
            (item) =>
                item.attributeAssociation.leftSideValue !== '' &&
                item.attributeAssociation.rightSideValue !== '' &&
                !item.leftSideError &&
                !item.rightSideError
        );

    const updateEmptyParameterInputErrors = (): void => {
        const updatedItems: AttributeAssociationItem[] =
            attributeAssociationList.map(
                (item: AttributeAssociationItem, index: number) => {
                    const isFirstItem: boolean = index === 0;
                    const leftSideValueValid: boolean =
                        !!item.attributeAssociation.leftSideValue;
                    const rightSideValueValid: boolean =
                        !!item.attributeAssociation.rightSideValue;
                    const displayBothEmpty: boolean =
                        !leftSideValueValid && !rightSideValueValid;

                    return {
                        ...item,
                        leftSideError: !leftSideValueValid,
                        leftSideErrorMessage: getEmptyErrorMessage(
                            isFirstItem,
                            displayBothEmpty,
                            !leftSideValueValid
                        ),
                        rightSideError: !rightSideValueValid,
                        rightSideErrorMessage: getEmptyErrorMessage(
                            isFirstItem,
                            displayBothEmpty,
                            !rightSideValueValid
                        ),
                    };
                }
            );
        handleAttributeAssociationsChanged(updatedItems);
    };
    const onAddButtonClick = (): void => {
        if (isAttributeAssociationNonEmpty) {
            handleAttributeAssociationsChanged([
                ...attributeAssociationList,
                {
                    ...defaultAttributeAssociationItem,
                    id: crypto.randomUUID(),
                },
            ]);
        } else {
            updateEmptyParameterInputErrors();
        }
    };

    const possiblyCheckForDuplicatedItems = (
        updatedItem: AttributeAssociationItem,
        isFirstItem: boolean
    ): AttributeAssociationItem => {
        const filteredItems = attributeAssociationList.filter(
            (item) =>
                item.attributeAssociation.leftSideValue ===
                    updatedItem.attributeAssociation.leftSideValue &&
                item.attributeAssociation.rightSideValue ===
                    updatedItem.attributeAssociation.rightSideValue
        );

        // found duplicated items.
        if (filteredItems.length > 0) {
            return {
                ...updatedItem,
                leftSideError: true,
                leftSideErrorMessage: EDGE_CONDITION_ERRORS.DUPLICATE_CONDITION,
                rightSideError: true,
                rightSideErrorMessage:
                    EDGE_CONDITION_ERRORS.DUPLICATE_CONDITION,
            };
        }

        const leftSideValueValid: boolean =
            !!updatedItem.attributeAssociation.leftSideValue;
        const rightSideValueValid: boolean =
            !!updatedItem.attributeAssociation.rightSideValue;
        const displayBothEmpty: boolean =
            !leftSideValueValid && !rightSideValueValid;

        // possibly check for empty values.
        return {
            ...updatedItem,
            leftSideError: !leftSideValueValid,
            leftSideErrorMessage: getEmptyErrorMessage(
                isFirstItem,
                displayBothEmpty,
                !leftSideValueValid
            ),
            rightSideError: !rightSideValueValid,
            rightSideErrorMessage: getEmptyErrorMessage(
                isFirstItem,
                displayBothEmpty,
                !rightSideValueValid
            ),
        };
    };

    const updateItem = (
        idToUpdate: string,
        updatedItem: AttributeAssociationItem
    ): void => {
        const updatedItems: AttributeAssociationItem[] =
            attributeAssociationList.map((item, index: number) =>
                idToUpdate === item.id
                    ? possiblyCheckForDuplicatedItems(updatedItem, index === 0)
                    : item
            );
        handleAttributeAssociationsChanged(updatedItems);
    };

    const deleteItem = (idToDelete: string): void => {
        const updatedItems: AttributeAssociationItem[] =
            attributeAssociationList.filter(
                (association) => idToDelete !== association.id
            );
        handleAttributeAssociationsChanged(updatedItems);
    };

    return (
        <>
            <Grid sx={attributeAssociationWidgetStyles.titleContainer}>
                <Typography
                    sx={
                        attributeAssociationWidgetStyles.attributeAssociationTitle
                    }
                >
                    Attribute Associations
                </Typography>
            </Grid>
            <Grid sx={attributeAssociationWidgetStyles.listContainer}>
                <List sx={attributeAssociationWidgetStyles.list}>
                    {attributeAssociationList.map(
                        (item: AttributeAssociationItem, index: number) => (
                            <AttributeAssociationSelection
                                key={item.id}
                                isFirstItem={index === 0}
                                attributeAssociationItem={item}
                                handleRemove={(): void => deleteItem(item.id)}
                                onAttributeAssociationChanged={(
                                    value: any
                                ): void => updateItem(item.id, value)}
                                leftSideSelectionOptions={
                                    leftSideSelectionOptions
                                }
                                rightSideSelectionOptions={
                                    rightSideSelectionOptions
                                }
                            />
                        )
                    )}
                </List>
            </Grid>
            <Grid sx={attributeAssociationWidgetStyles.addButtonContainer}>
                <Fab
                    type='button'
                    sx={attributeAssociationWidgetStyles.addButton}
                    icon={<AddIcon />}
                    color='secondary'
                    label='Attribute Association'
                    variant='extended'
                    size='small'
                    onClick={onAddButtonClick}
                />
            </Grid>
        </>
    );
};
